import Phaser from "phaser";

import { TROPHY_HALL_ART } from "../config/artAssets";
import type { AchievementId, ProgressSegment, SeasonStatus } from "../domain/types";

export type TrophyCelebration = "daily" | "achievement" | "season";

export type TrophyHallVisualState = {
  segment: ProgressSegment;
  dailyCompleteCount: number;
  dailyClaimed: boolean;
  featuredAchievement: AchievementId;
  featuredAchievementState: "locked" | "ready" | "claimed";
  seasonStatus: SeasonStatus;
  tonUsdtPrizeEnabled: boolean;
  channelJoined: boolean;
  chatJoined: boolean;
  reducedMotion: boolean;
};

export type TrophyHallSceneCallbacks = {
  onReady?(): void;
};

type ArtAsset = { key: string; url: string; width: number; height: number };
type DailyCrystal = {
  dimmer: Phaser.GameObjects.Graphics;
  light: Phaser.GameObjects.Graphics;
};

const HERO_HEIGHT = {
  today: 196,
  achievements: 174,
  season: 204,
} as const;

const ACHIEVEMENT_IDS: readonly AchievementId[] = [
  "first-spark",
  "living-facet",
  "master-rhythm",
  "light-architect",
  "makers-circle",
  "perfect-facet",
  "crystal-reserve",
  "silent-current",
  "arena-guardian",
  "season-one-participant",
  "season-two-participant",
  "streak-seven",
  "streak-fifteen",
  "streak-thirty",
  "chain-conqueror",
  "branch-master",
  "workshop-sovereign",
  "pvp-first-win",
  "pvp-ten-wins",
  "arena-ten-matches",
  "arena-fifty-matches",
  "active-friend-one",
  "active-friend-three",
  "active-friend-ten",
  "top-ten-percent",
  "top-hundred",
  "top-ten",
  "season-champion",
];

export class TrophyHallScene extends Phaser.Scene {
  readonly #callbacks: TrophyHallSceneCallbacks;
  readonly #renderScale: number;
  #visualState: TrophyHallVisualState;
  #layoutWidth = 0;
  #layoutHeight = 0;
  #heroBaseY = 0;
  #pendingCelebration: TrophyCelebration | null = null;

  #background!: Phaser.GameObjects.Image;
  #ambientShade!: Phaser.GameObjects.Rectangle;
  #hero!: Phaser.GameObjects.Container;
  #heroGlow!: Phaser.GameObjects.Arc;
  #heroShadow!: Phaser.GameObjects.Ellipse;
  #dailySeal!: Phaser.GameObjects.Image;
  #seasonCrown!: Phaser.GameObjects.Image;
  #achievementImages = {} as Record<AchievementId, Phaser.GameObjects.Image>;
  #dailyCrystals: DailyCrystal[] = [];
  #seasonStones: Phaser.GameObjects.Arc[] = [];
  #walletPrizeGems: Phaser.GameObjects.Polygon[] = [];
  #sparks: Phaser.GameObjects.Arc[] = [];
  #flash!: Phaser.GameObjects.Rectangle;
  #ambientTweens: Phaser.Tweens.Tween[] = [];

  constructor(
    callbacks: TrophyHallSceneCallbacks,
    visualState: TrophyHallVisualState,
    renderScale: number,
  ) {
    super({ key: "TrophyHall" });
    this.#callbacks = callbacks;
    this.#visualState = visualState;
    this.#renderScale = renderScale;
  }

  preload(): void {
    this.load.image(TROPHY_HALL_ART.background.key, TROPHY_HALL_ART.background.url);
    this.load.image(TROPHY_HALL_ART.dailySeal.key, TROPHY_HALL_ART.dailySeal.url);
    this.load.image(TROPHY_HALL_ART.seasonCrown.key, TROPHY_HALL_ART.seasonCrown.url);
    for (const id of ACHIEVEMENT_IDS) {
      const asset = TROPHY_HALL_ART.achievements[id];
      this.load.image(asset.key, asset.url);
    }
  }

  create(): void {
    this.#background = this.add.image(0, 0, TROPHY_HALL_ART.background.key).setDepth(0);
    this.#ambientShade = this.add.rectangle(0, 0, 10, 10, 0x062a35, 0.12).setOrigin(0).setDepth(1);
    this.#heroGlow = this.add
      .circle(0, -2, 72, 0x4ce1e1, 0.18)
      .setBlendMode(Phaser.BlendModes.ADD);
    this.#heroShadow = this.add
      .ellipse(0, 83, 190, 30, 0x05232b, 0.5)
      .setBlendMode(Phaser.BlendModes.MULTIPLY);
    this.#dailySeal = this.add.image(0, 0, TROPHY_HALL_ART.dailySeal.key).setVisible(false);
    for (const id of ACHIEVEMENT_IDS) {
      this.#achievementImages[id] = this.add.image(0, 0, TROPHY_HALL_ART.achievements[id].key).setVisible(false);
    }
    this.#seasonCrown = this.add.image(0, 0, TROPHY_HALL_ART.seasonCrown.key).setVisible(false);

    const crystalPolygons = [
      [
        new Phaser.Geom.Point(0, -72), new Phaser.Geom.Point(18, -61),
        new Phaser.Geom.Point(18, -35), new Phaser.Geom.Point(0, -20),
        new Phaser.Geom.Point(-18, -35), new Phaser.Geom.Point(-18, -61),
      ],
      [
        new Phaser.Geom.Point(-76, 32), new Phaser.Geom.Point(-48, 17),
        new Phaser.Geom.Point(-20, 31), new Phaser.Geom.Point(-24, 54),
        new Phaser.Geom.Point(-50, 65), new Phaser.Geom.Point(-76, 53),
      ],
      [
        new Phaser.Geom.Point(20, 31), new Phaser.Geom.Point(48, 17),
        new Phaser.Geom.Point(76, 32), new Phaser.Geom.Point(76, 53),
        new Phaser.Geom.Point(50, 65), new Phaser.Geom.Point(24, 54),
      ],
    ];
    this.#dailyCrystals = crystalPolygons.map((points) => {
      const dimmer = this.add.graphics().setBlendMode(Phaser.BlendModes.MULTIPLY).setVisible(false);
      dimmer.fillStyle(0x07343d, 0.82).fillPoints(points, true);
      const light = this.add.graphics().setBlendMode(Phaser.BlendModes.ADD).setVisible(false);
      light.fillGradientStyle(0x81fff1, 0xffd77f, 0x5be8df, 0xffc86c, 0.72);
      light.fillPoints(points, true);
      return { dimmer, light };
    });

    this.#seasonStones = Array.from({ length: 10 }, (_, index) =>
      this.add
        .circle(-58 + index * (116 / 9), 77, 3.2, index < 5 ? 0xffd27a : 0xb5fbf5, 0)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setVisible(false),
    );
    const prizeGemPositions = [
      [-62, 54], [-38, 44], [-13, 38], [13, 38], [38, 44], [62, 54],
    ] as const;
    this.#walletPrizeGems = prizeGemPositions.map(([x, y]) =>
      this.add
        .polygon(x, y, [0, -5, 4.5, 0, 0, 5, -4.5, 0], 0xffd170, 0.95)
        .setStrokeStyle(1, 0xfff2c2, 0.9)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setVisible(false),
    );

    this.#hero = this.add.container(0, 0, [
      this.#heroShadow,
      this.#heroGlow,
      this.#dailySeal,
      ...ACHIEVEMENT_IDS.map((id) => this.#achievementImages[id]),
      this.#seasonCrown,
      ...this.#dailyCrystals.flatMap(({ dimmer, light }) => [dimmer, light]),
      ...this.#seasonStones,
      ...this.#walletPrizeGems,
    ]).setDepth(4);

    this.#sparks = Array.from({ length: 28 }, (_, index) =>
      this.add
        .circle(0, 0, index % 5 === 0 ? 3 : 2, index % 3 === 0 ? 0xffce72 : 0x8dffff, 1)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setDepth(7)
        .setVisible(false),
    );
    this.#flash = this.add.rectangle(0, 0, 10, 10, 0xffe2a4, 0).setOrigin(0).setDepth(8);

    this.scale.on(Phaser.Scale.Events.RESIZE, this.#layout, this);
    this.#layout({ width: this.scale.width, height: this.scale.height } as Phaser.Structs.Size);
    this.#applyVisualState(false);
    this.#callbacks.onReady?.();
    if (this.#pendingCelebration !== null) this.playCelebration(this.#pendingCelebration);
  }

  setVisualState(visualState: TrophyHallVisualState): void {
    if (
      this.#visualState.segment === visualState.segment &&
      this.#visualState.dailyCompleteCount === visualState.dailyCompleteCount &&
      this.#visualState.dailyClaimed === visualState.dailyClaimed &&
      this.#visualState.featuredAchievement === visualState.featuredAchievement &&
      this.#visualState.featuredAchievementState === visualState.featuredAchievementState &&
      this.#visualState.seasonStatus === visualState.seasonStatus &&
      this.#visualState.tonUsdtPrizeEnabled === visualState.tonUsdtPrizeEnabled &&
      this.#visualState.channelJoined === visualState.channelJoined &&
      this.#visualState.chatJoined === visualState.chatJoined &&
      this.#visualState.reducedMotion === visualState.reducedMotion
    ) return;
    this.#visualState = visualState;
    if (this.sys.isActive()) this.#applyVisualState(true);
  }

  playCelebration(kind: TrophyCelebration): void {
    if (!this.sys.isActive() || this.#visualState.reducedMotion) {
      this.#pendingCelebration = this.sys.isActive() ? null : kind;
      return;
    }
    this.#pendingCelebration = null;
    this.#ambientTweens.forEach((tween) => tween.stop());
    this.#ambientTweens = [];
    this.tweens.killTweensOf(this.#hero);
    this.tweens.killTweensOf(this.#heroGlow);
    this.tweens.killTweensOf(this.#flash);

    this.#hero.setY(this.#heroBaseY + 7).setScale(0.93);
    this.#heroGlow.setAlpha(0.78).setScale(0.82);
    this.#flash.setAlpha(0.22);
    this.tweens.add({ targets: this.#flash, alpha: 0, duration: 520, ease: "Quad.easeOut" });
    this.tweens.add({
      targets: this.#hero,
      y: this.#heroBaseY,
      scaleX: 1,
      scaleY: 1,
      duration: 620,
      ease: "Back.easeOut",
      onComplete: () => this.#restartAmbientMotion(),
    });
    this.tweens.add({
      targets: this.#heroGlow,
      alpha: kind === "season" ? 0.44 : 0.36,
      scale: kind === "achievement" ? 1.2 : 1.08,
      duration: 760,
      ease: "Cubic.easeOut",
    });

    if (kind === "daily") {
      this.#dailyCrystals.slice(0, this.#visualState.dailyCompleteCount).forEach(({ light }, index) => {
        light.setVisible(true).setAlpha(0.18);
        this.tweens.add({
          targets: light,
          alpha: 0.78,
          duration: 340,
          delay: index * 120,
          yoyo: true,
          ease: "Sine.easeOut",
        });
      });
    }
    if (kind === "season") {
      this.#seasonStones.forEach((stone, index) => {
        stone.setVisible(true).setAlpha(0.15);
        this.tweens.add({
          targets: stone,
          alpha: 1,
          scale: 1.7,
          duration: 200,
          delay: index * 65,
          yoyo: true,
          ease: "Quad.easeOut",
        });
      });
    }
    this.#emitBurst(kind === "season" ? 34 : 26);
  }

  debugSnapshot(): Record<string, unknown> {
    return {
      segment: this.#visualState.segment,
      dailyCompleteCount: this.#visualState.dailyCompleteCount,
      dailyClaimed: this.#visualState.dailyClaimed,
      dormantCrystals: this.#dailyCrystals.map(({ dimmer }) => dimmer.visible),
      litCrystals: this.#dailyCrystals.map(({ light }) => light.visible),
      crystalLightAlpha: this.#dailyCrystals.map(({ light }) => Number(light.alpha.toFixed(3))),
      heroY: Number(this.#hero.y.toFixed(2)),
      heroBaseY: this.#heroBaseY,
      reducedMotion: this.#visualState.reducedMotion,
      communityStones: [this.#visualState.channelJoined, this.#visualState.chatJoined],
      walletPrizeGems: this.#walletPrizeGems.map((gem) => ({ visible: gem.visible, alpha: Number(gem.alpha.toFixed(3)) })),
    };
  }

  #layout(size: Phaser.Structs.Size): void {
    const physicalWidth = Math.round(size.width);
    const physicalHeight = Math.round(size.height);
    const width = Math.round(physicalWidth / this.#renderScale);
    const height = Math.round(physicalHeight / this.#renderScale);
    if (width <= 0 || height <= 0 || (width === this.#layoutWidth && height === this.#layoutHeight)) return;
    this.#layoutWidth = width;
    this.#layoutHeight = height;
    this.cameras.main
      .setViewport(0, 0, physicalWidth, physicalHeight)
      .setZoom(this.#renderScale)
      .centerOn(width / 2, height / 2);

    const backgroundScale = Math.max(
      width / TROPHY_HALL_ART.background.width,
      height / TROPHY_HALL_ART.background.height,
    );
    this.#background
      .setPosition(width / 2, height / 2 - Math.min(46, height * 0.13))
      .setScale(backgroundScale);
    this.#ambientShade.setSize(width, height);
    this.#flash.setSize(width, height);
    this.#heroBaseY = Math.round(height * (height <= 300 ? 0.69 : 0.68));
    this.#hero.setPosition(width / 2, this.#heroBaseY);
    this.#applyVisualState(false);
  }

  #applyVisualState(animateTransition: boolean): void {
    this.#ambientTweens.forEach((tween) => tween.stop());
    this.#ambientTweens = [];
    this.tweens.killTweensOf(this.#hero);
    this.#dailySeal.setVisible(false);
    this.#seasonCrown.setVisible(false);
    ACHIEVEMENT_IDS.forEach((id) => this.#achievementImages[id].setVisible(false).clearTint().setAlpha(1));
    this.#dailyCrystals.forEach(({ dimmer, light }) => {
      dimmer.setVisible(false).setAlpha(1).setScale(1);
      light.setVisible(false).setAlpha(0).setScale(1);
    });
    this.#seasonStones.forEach((stone) => stone.setVisible(false).setAlpha(0).setScale(1));
    this.#walletPrizeGems.forEach((gem) => gem.setVisible(false).setAlpha(0).setScale(1));
    this.#heroGlow.setScale(1).setAlpha(0.2).setFillStyle(0x63e6e0, 0.18);
    this.#heroShadow.setScale(1).setAlpha(0.52);

    if (this.#visualState.segment === "today") this.#showDaily();
    else if (this.#visualState.segment === "achievements") this.#showAchievement();
    else this.#showSeason();

    this.#hero.setY(this.#heroBaseY).setScale(animateTransition && !this.#visualState.reducedMotion ? 0.94 : 1).setAlpha(1);
    if (animateTransition && !this.#visualState.reducedMotion) {
      this.tweens.add({
        targets: this.#hero,
        scaleX: 1,
        scaleY: 1,
        duration: 380,
        ease: "Back.easeOut",
        onComplete: () => this.#restartAmbientMotion(),
      });
    } else {
      this.#restartAmbientMotion();
    }
  }

  #showDaily(): void {
    this.#fitImage(this.#dailySeal, TROPHY_HALL_ART.dailySeal, HERO_HEIGHT.today);
    this.#dailySeal.setVisible(true).clearTint().setAlpha(this.#visualState.dailyClaimed ? 0.96 : 1);
    const completeCount = Phaser.Math.Clamp(this.#visualState.dailyCompleteCount, 0, 3);
    const compactScale = Phaser.Math.Clamp(this.#layoutWidth / 360, 0.86, 1.12);
    this.#dailyCrystals.forEach(({ dimmer, light }, index) => {
      const complete = index < completeCount;
      dimmer.setScale(compactScale).setVisible(!complete).setAlpha(this.#visualState.dailyClaimed ? 0 : 0.84);
      light.setScale(compactScale).setVisible(complete).setAlpha(this.#visualState.dailyClaimed ? 0.48 : 0.28);
    });
    this.#heroGlow
      .setFillStyle(this.#visualState.dailyClaimed ? 0xffd483 : 0x63e6e0, 0.2)
      .setAlpha(this.#visualState.dailyClaimed ? 0.35 : 0.2);
  }

  #showAchievement(): void {
    const id = this.#visualState.featuredAchievement;
    const image = this.#achievementImages[id];
    this.#fitImage(image, TROPHY_HALL_ART.achievements[id], HERO_HEIGHT.achievements);
    image.setVisible(true);
    if (this.#visualState.featuredAchievementState === "locked") {
      image.setTint(0x6d8f91).setAlpha(0.7);
      this.#heroGlow.setFillStyle(0x5b9698, 0.14).setAlpha(0.13);
    } else if (this.#visualState.featuredAchievementState === "ready") {
      image.clearTint().setAlpha(1);
      this.#heroGlow.setFillStyle(0xffcc72, 0.26).setAlpha(0.4);
    } else {
      image.clearTint().setAlpha(1);
      this.#heroGlow.setFillStyle(0x72eee5, 0.22).setAlpha(0.32);
    }
  }

  #showSeason(): void {
    this.#fitImage(this.#seasonCrown, TROPHY_HALL_ART.seasonCrown, HERO_HEIGHT.season);
    this.#seasonCrown.setVisible(true).clearTint().setAlpha(1);
    const status = this.#visualState.seasonStatus;
    if (status === "scheduled") this.#seasonCrown.setTint(0x78989a).setAlpha(0.72);
    if (status === "frozen") this.#seasonCrown.setTint(0xc7e7e4).setAlpha(0.88);
    if (status === "closed") this.#seasonCrown.setAlpha(0.9);
    const lit = status === "scheduled" ? 0 : status === "active" ? 6 : 8;
    this.#seasonStones.forEach((stone, index) => {
      const communityStone = index >= 8;
      const communityJoined = index === 8 ? this.#visualState.channelJoined : this.#visualState.chatJoined;
      const stoneLit = communityStone ? communityJoined : index < lit;
      stone
        .setVisible(true)
        .setFillStyle(
          communityStone && communityJoined ? 0xffd377 : status === "claimable" ? 0xffd377 : 0x9efcf1,
          stoneLit ? 0.85 : 0.1,
        )
        .setAlpha(stoneLit ? 0.82 : 0.12);
    });
    this.#walletPrizeGems.forEach((gem, index) => {
      gem
        .setVisible(this.#visualState.tonUsdtPrizeEnabled)
        .setFillStyle(index === 0 ? 0xffd16f : 0x8ef8ec, 0.94)
        .setAlpha(this.#visualState.tonUsdtPrizeEnabled ? 0.86 : 0);
    });
    this.#heroGlow
      .setFillStyle(status === "claimable" ? 0xffc45f : 0x62e8df, 0.24)
      .setAlpha(status === "claimable" ? 0.48 : status === "scheduled" ? 0.1 : 0.3);
  }

  #fitImage(image: Phaser.GameObjects.Image, asset: ArtAsset, targetHeight: number): void {
    const compactScale = Phaser.Math.Clamp(this.#layoutWidth / 360, 0.86, 1.12);
    const height = targetHeight * compactScale;
    image.setScale(height / asset.height);
  }

  #restartAmbientMotion(): void {
    this.#ambientTweens.forEach((tween) => tween.stop());
    this.#ambientTweens = [];
    this.tweens.killTweensOf(this.#hero);
    this.tweens.killTweensOf(this.#heroGlow);
    this.#hero.setY(this.#heroBaseY).setAngle(0);
    if (this.#visualState.reducedMotion) return;

    this.#ambientTweens.push(
      this.tweens.add({
        targets: this.#hero,
        y: this.#heroBaseY - 5,
        duration: 2500,
        yoyo: true,
        repeat: -1,
        ease: "Sine.easeInOut",
      }),
      this.tweens.add({
        targets: this.#heroGlow,
        alpha: Math.min(0.58, this.#heroGlow.alpha + 0.12),
        scale: 1.08,
        duration: 1800,
        yoyo: true,
        repeat: -1,
        ease: "Sine.easeInOut",
      }),
    );

    if (this.#visualState.segment === "today") {
      this.#dailyCrystals.slice(0, this.#visualState.dailyCompleteCount).forEach(({ light }, index) => {
        this.#ambientTweens.push(
          this.tweens.add({
            targets: light,
            alpha: this.#visualState.dailyCompleteCount === 3 ? 0.68 : 0.5,
            duration: 1850 + index * 170,
            delay: index * 240,
            yoyo: true,
            repeat: -1,
            ease: "Sine.easeInOut",
          }),
        );
      });
    }

    if (this.#visualState.segment === "season" && this.#visualState.seasonStatus === "active") {
      this.#seasonStones.filter((_, index) => index < 6 || (index === 8 && this.#visualState.channelJoined) || (index === 9 && this.#visualState.chatJoined)).forEach((stone, index) => {
        this.#ambientTweens.push(
          this.tweens.add({
            targets: stone,
            alpha: 1,
            scale: 1.38,
            duration: 520,
            delay: index * 150,
            hold: 260,
            yoyo: true,
            repeat: -1,
            repeatDelay: 900,
            ease: "Sine.easeInOut",
          }),
        );
      });
      if (this.#visualState.tonUsdtPrizeEnabled) {
        this.#walletPrizeGems.forEach((gem, index) => {
          this.#ambientTweens.push(
            this.tweens.add({
              targets: gem,
              alpha: 1,
              scale: 1.34,
              duration: 900,
              delay: index * 170,
              yoyo: true,
              repeat: -1,
              repeatDelay: 1_100,
              ease: "Sine.easeInOut",
            }),
          );
        });
      }
    }
  }

  #emitBurst(count: number): void {
    const total = Math.min(count, this.#sparks.length);
    for (let index = 0; index < total; index += 1) {
      const spark = this.#sparks[index];
      if (spark === undefined) continue;
      const angle = (Math.PI * 2 * index) / total + (index % 2) * 0.09;
      const distance = 54 + (index % 7) * 8;
      this.tweens.killTweensOf(spark);
      spark
        .setPosition(this.#hero.x, this.#hero.y - 8)
        .setVisible(true)
        .setAlpha(1)
        .setScale(index % 4 === 0 ? 1.25 : 0.82);
      this.tweens.add({
        targets: spark,
        x: this.#hero.x + Math.cos(angle) * distance,
        y: this.#hero.y - 8 + Math.sin(angle) * distance * 0.68 - 12,
        alpha: 0,
        scale: 0.35,
        duration: 560 + (index % 5) * 55,
        ease: "Cubic.easeOut",
        onComplete: () => spark.setVisible(false),
      });
    }
  }
}
